import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChatAiLoadingIcon } from '@/features/prince/messages/ChatAiLoadingIcon';
import { cn } from '@/utils/helpers';
import { Send, Square, X } from 'lucide-react';
import { forwardRef, useImperativeHandle, useState, useRef, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ChatInputProps {
  placeholder: string;
  onSend: (message: string) => void;
  isSending?: boolean;
  onAbortSending?: () => void;
  attachments?: Array<{
    filename: string;
    removeLabel: string;
    loading: boolean;
    onRemove: () => void;
  }>;
  actionButtons?: Array<{
    key: string;
    icon?: ReactNode;
    label: string;
    onClick: () => void;
  }>;
  disabled: boolean;
  /**
   * If true, disables only the send button (textarea and action buttons remain enabled).
   * Note: This has no effect when `disabled` is true, as the entire component is disabled.
   */
  disableSend?: boolean;
  /**
   * Optional callback when files are pasted into the textarea
   */
  onPaste?: (files: File[]) => void;
}

export type ChatInputRef = {
  clear: () => void;
};

const MIN_HEIGHT = 44;
const MAX_HEIGHT = 176;

export const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(function ChatInputWithRef(
  {
    placeholder,
    onSend,
    attachments,
    actionButtons,
    disabled = false,
    isSending,
    onAbortSending,
    disableSend = false,
    onPaste: onPasteCallback,
  }: ChatInputProps,
  ref
) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      clear: () => {
        setMessage('');
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      },
    }),
    []
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
    
    textarea.style.height = `${newHeight}px`;
  }, [message]);

  const isSendDisabled = disabled || !message.trim() || disableSend;

  const handleSend = () => {
    onSend(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSendDisabled) handleSend();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onPasteCallback) return;

    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const items = Array.from(clipboardData.items);
    const imageFiles: File[] = [];

    // Extract image files from clipboard
    items.forEach(item => {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    });

    // If we found images, prevent default paste behavior and notify parent
    if (imageFiles.length > 0) {
      e.preventDefault();
      onPasteCallback(imageFiles);
    }
  };

  const attachmentPill = (
    a: { filename: string; removeLabel: string; loading: boolean; onRemove: () => void },
    idx: number
  ) => (
    <Badge
      key={`attachment-${idx}`}
      variant="secondary"
      className="flex items-center gap-1 max-w-full min-w-0"
      data-testid={`attachment-pill-${a.filename || idx}`}
    >
      {a.loading ? (
        <ChatAiLoadingIcon dataTestId={`attachment-pill-loading-${a.filename || idx}`} />
      ) : (
        <>
          <span className="truncate min-w-0 flex-1" title={a.filename}>
            {a.filename}
          </span>
          <button
            type="button"
            onClick={a.onRemove}
            className="ml-1 hover:text-red-600 flex-shrink-0"
            title={a.removeLabel}
            aria-label={a.removeLabel}
          >
            <X className="h-3 w-3" />
          </button>
        </>
      )}
    </Badge>
  );

  return (
    <div className="p-1 w-full max-w-[677px] flex flex-col gap-1 rounded-lg bg-gradient-to-t from-gray-100 to-white drop-shadow-[0_1px_7px_rgba(0,0,0,0.23)]">
      <div className="bg-white rounded-md">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full pt-2.5 pl-4 pr-3 text-sm overflow-y-auto',
            'resize-none placeholder:text-gray-500 focus:outline-none',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          style={{ 
            scrollbarGutter: 'stable',
            minHeight: `${MIN_HEIGHT}px`,
            maxHeight: `${MAX_HEIGHT}px`
          }}
          rows={1}
          data-testid="chat-input-textarea"
        />
        {/* Attached files pills */}
        {attachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 px-3 mb-2 min-w-0 max-w-full overflow-hidden">
            {attachments.map(attachmentPill)}
          </div>
        )}
      </div>
      <div
        className={cn(
          'flex items-center gap-2 py-1 pl-1 pr-3',
          actionButtons?.length ? 'justify-between' : 'justify-end'
        )}
      >
        {actionButtons?.map(a => (
          <Button
            key={a.key}
            variant="ghost-gray"
            onClick={a.onClick}
            disabled={disabled}
            className="h-8 gap-1 !px-3 text-text-secondary"
            data-testid={`action-button-${a.key}`}
          >
            {a.icon}
            <span className="pb-0.5 text-text-secondary">{a.label}</span>
          </Button>
        ))}
        {isSending && onAbortSending ? (
          <Button size="sm" onClick={onAbortSending} disabled={disabled} className="gap-2">
            <Square className="h-3.5 w-3.5 text-white bg-white rounded-sm" />
          </Button>
        ) : (
          <Button
            onClick={handleSend}
            disabled={isSendDisabled}
            size="sm"
            className="gap-2 bg-gradient-to-r from-[#043764] via-[#193446] to-[#22333B] border-none hover:opacity-90 align-end justify-end"
            title={t('buttons.send')}
            aria-label={t('buttons.send')}
            data-testid="send-button"
          >
            {t('buttons.send')}
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
});
